import {
  WSDivider,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSFormOld,
  WSInputMaskOld,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { OtpVerificationChannel } from "@wingspanhq/users/dist/lib/interfaces";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useWSMutation } from "../../query/helpers";
import { ErrorContextKey } from "../../services/platform";
import { usersService } from "../../services/users";
import { OTPDisclosure } from "../../shared/components/OPTDisclosure";
import { validatorOTP } from "../../shared/validators/validatorOTP";
import { WSServiceError } from "../../utils/serviceHelper";
import { WSErrorMessage } from "../WSErrorMessage/WSErrorMessage";

const RESEND_COUNTDOWN_SECONDS = 60;

type FormData = { code: string };

type Props = {
  onSuccess: () => void;
} & WSElementProps;

const formatCountdown = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const SmsConfirmationForm: React.FC<Props> = ({
  onSuccess,
  ...elementProps
}) => {
  const location = useLocation();
  const [countdown, setCountdown] = useState(RESEND_COUNTDOWN_SECONDS);

  const [submit, submitMeta] = useWSMutation<void, WSServiceError, FormData>(
    async data => {
      await usersService.session.createOtpSession({
        code: data.code
      });
    },
    {
      onSuccess
    }
  );

  const [resend, resendMeta] = useWSMutation<void, WSServiceError, void>(
    async () => {
      await usersService.session.beginOtpSession({
        channel: OtpVerificationChannel.SMS,
        appRedirectPath: `${location.pathname}${location.search}`
      });
    },
    {
      onSuccess: () => {
        setCountdown(RESEND_COUNTDOWN_SECONDS);
      }
    }
  );

  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const handleResend = useCallback(() => {
    resend();
  }, [resend]);

  const isResendDisabled = countdown > 0 || resendMeta.isLoading;

  return (
    <WSElement {...elementProps}>
      <WSFormOld<FormData>
        onSubmit={submit}
        validationSchema={Yup.object().shape({
          code: validatorOTP
        })}
      >
        <WSList gap="XL">
          <WSText color="gray600">
            A 6 digit verification code has been sent via SMS to your registered
            mobile number.
          </WSText>

          <WSFormOld.Field
            label="Verification code"
            name="code"
            component={WSInputMaskOld}
            componentProps={{
              autoComplete: "one-time-code",
              mask: "999999",
              slotChar: ""
            }}
          />
          <OTPDisclosure />
          <WSErrorMessage
            error={submitMeta.error}
            contextKey={ErrorContextKey.CreateOtpSession}
          />
          <WSButton
            name="confirmTwoFactorAuth"
            fullWidth
            loading={submitMeta.isLoading}
          >
            Continue
          </WSButton>
        </WSList>
      </WSFormOld>

      <WSDivider mt="XL" />

      <WSElement mt="XL">
        <WSText color="gray500" mb="M" align="center">
          Didn't receive it?
        </WSText>

        <WSFlexBox justify="center" mb={countdown > 0 ? "S" : "NONE"}>
          <WSButton.Secondary
            size="S"
            disabled={isResendDisabled}
            loading={resendMeta.isLoading}
            onClick={handleResend}
            type="button"
          >
            Resend code
          </WSButton.Secondary>
        </WSFlexBox>

        {countdown > 0 && (
          <WSText weight="bold" color="gray500" align="center">
            in {formatCountdown(countdown)}
          </WSText>
        )}

        <WSErrorMessage
          mt="M"
          error={resendMeta.error}
          contextKey={ErrorContextKey.VerifyPhone}
        />
      </WSElement>
    </WSElement>
  );
};
